import manifest from '../../content/dcc-core/manifest.json';
import melee from '../../content/dcc-core/skills/melee-attacks.json';
import ranged from '../../content/dcc-core/skills/ranged-attacks.json';
import utility from '../../content/dcc-core/skills/utility.json';
import passive from '../../content/dcc-core/skills/passive.json';
import damageEffects from '../../content/dcc-core/skills/damage-effects.json';
import attackSpells from '../../content/dcc-core/spells/attack-spells.json';
import supportSpells from '../../content/dcc-core/spells/support-spells.json';
import humanBackgrounds from '../../content/dcc-core/backgrounds/human.json';
import animalBackgrounds from '../../content/dcc-core/backgrounds/animal.json';
import startingCombat from '../../content/dcc-core/starting-combat.json';
import attacks from '../../content/dcc-core/attacks.json';
import coreItems from '../../content/dcc-core/items/core.json';
import type {
  AttackContent,
  AttackProfile,
  Background,
  BackgroundTable,
  ContentPackage,
  Item,
  Skill,
  StartingCombatContent,
} from './types.ts';
import { weaponLinkProblem } from '../rules/inventory.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import { ContentRegistry } from './registry.ts';

/**
 * The one place the application binds to a specific content package.
 * Swap the imports here to load a different ruleset's content.
 */
export const dccCorePackage: ContentPackage = {
  packageId: manifest.packageId,
  name: manifest.name,
  rulesetVersion: manifest.rulesetVersion,
  skills: [
    ...melee,
    ...ranged,
    ...utility,
    ...passive,
    ...damageEffects,
    ...attackSpells,
    ...supportSpells,
  ] as Skill[],
  backgroundTables: [
    ...humanBackgrounds.tables,
    ...animalBackgrounds.tables,
  ] as BackgroundTable[],
  backgrounds: [
    ...humanBackgrounds.backgrounds,
    ...animalBackgrounds.backgrounds,
  ] as Background[],
  startingCombat: startingCombat as StartingCombatContent,
  attacks: attacks as unknown as AttackContent,
  items: coreItems as unknown as Item[],
};

/**
 * The catalogue the whole application reads.
 *
 * It is a `let` with setters rather than a constant because the player's own
 * content is part of the catalogue and is loaded from disk after startup. ES
 * module imports are live bindings, so every `import { contentRegistry }` in
 * the application sees the replacement without any of them being changed.
 *
 * The registry object itself stays immutable: the catalogue is REPLACED, never
 * mutated, so a half-updated catalogue cannot be observed.
 *
 * Custom content goes into the same lists as the printed content on purpose.
 * Everything downstream - Checks, advancement, Effects, the Edit screen, the
 * creator - then treats it as ordinary content, because to those systems it is.
 */
export let contentRegistry = new ContentRegistry(dccCorePackage);

/**
 * A counter bumped whenever the catalogue changes. The interface uses it to
 * re-render, since replacing a module binding is invisible to React.
 */
export let contentVersion = 0;

/**
 * Everything the player has authored, held as ONE state.
 *
 * This is deliberately a single record rather than a variable per content type.
 * With separate setters each rebuilding the catalogue from the core package,
 * loading Skills and then Backgrounds would silently discard the Skills - the
 * second setter would have no idea the first had run. Composition reads the
 * whole record every time, so updating one collection preserves the others by
 * construction rather than by every caller remembering to pass them again.
 *
 * Adding a content type means adding a field here and a line in
 * `composeContentPackage`. It does NOT mean adding another setter that starts
 * from `dccCorePackage`.
 */
export interface CustomContent {
  skills: readonly Skill[];
  /** Attack companions for accepted custom Skills. Not independent global IDs. */
  attacks: readonly AttackProfile[];
  backgroundTables: readonly BackgroundTable[];
  backgrounds: readonly Background[];
  /**
   * Custom Items, authored in the Custom Items screen (Item 5 Slice B). They
   * join the global `custom:` ID namespace, composition, and the portability
   * resolver exactly as custom Skills and Backgrounds do.
   */
  items: readonly Item[];
}

export const EMPTY_CUSTOM_CONTENT: CustomContent = {
  skills: [],
  attacks: [],
  backgroundTables: [],
  backgrounds: [],
  items: [],
};

let customContent: CustomContent = EMPTY_CUSTOM_CONTENT;

/** What the catalogue currently holds of the player's own content. */
export function currentCustomContent(): CustomContent {
  return customContent;
}

/** One piece of custom content that was refused, and why. */
export interface Rejection<T> {
  item: T;
  reason: string;
}

/** Content refused during composition, by type. */
export interface RejectedContent {
  skills: Rejection<Skill>[];
  backgroundTables: Rejection<BackgroundTable>[];
  backgrounds: Rejection<Background>[];
  items: Rejection<Item>[];
}

function isEmpty(rejected: RejectedContent): boolean {
  return (
    rejected.skills.length === 0 &&
    rejected.backgroundTables.length === 0 &&
    rejected.backgrounds.length === 0 &&
    rejected.items.length === 0
  );
}

/**
 * RULE DCC-CUSTOM-002 - custom content whose ID collides with printed content,
 * or with another piece of custom content, is DROPPED rather than allowed to
 * shadow the book.
 *
 * RULE DCC-CUSTOM-009 - custom content whose DEPENDENCIES do not resolve in the
 * composed catalogue is dropped too. A Background must belong to a table that
 * exists and offer only Skills that exist, or the creator would present a
 * choice it cannot complete: picking it would grant a Skill nothing can look
 * up. Dropping is the honest outcome. The alternatives are inventing a
 * placeholder Skill, which fabricates content, or letting the Background
 * through, which produces a crawler whose starting Skills do not exist.
 *
 * Nothing is repaired and no file is touched. Restore the missing Skill and the
 * Background composes normally on the next load.
 */
export function composeContentPackage(
  base: ContentPackage,
  custom: CustomContent,
): { pkg: ContentPackage; rejected: RejectedContent } {
  const rejected: RejectedContent = {
    skills: [],
    backgroundTables: [],
    backgrounds: [],
    items: [],
  };

  // Returns accepted and rejected separately. Working out rejections by
  // filtering on object identity afterwards would miss a list that contains the
  // SAME object twice, which is exactly the duplicate case being guarded.
  /**
   * RULE DCC-CUSTOM-011 - the `custom:` namespace is GLOBAL across every kind
   * of player-authored content, not one namespace per type.
   *
   * A custom Skill and a custom Background sharing an ID makes identity
   * ambiguous, and the portable exporter has to resolve a bare `custom:` ID
   * without knowing what type it is. With a collision it would find whichever
   * lookup ran first and quietly omit the other definition from an export that
   * calls itself self-contained.
   *
   * Both sides are refused rather than one being picked or renamed. Picking
   * would make the export silently wrong in a different way, and renaming would
   * break every crawler that references the ID. The reason names the other
   * definition so the owner can see what collided with what.
   *
   * Scanning a flat list of every custom item is what makes this general: a
   * future custom Item type joins by being added to `everyCustomItem` below,
   * and needs no check of its own.
   */
  const everyCustomItem: { id: string; name: string; type: string }[] = [
    ...custom.skills.map((s) => ({ id: s.id, name: s.name, type: 'Skill' })),
    ...custom.backgroundTables.map((t) => ({ id: t.id, name: t.label, type: 'Background table' })),
    ...custom.backgrounds.map((b) => ({ id: b.id, name: b.name, type: 'Background' })),
    // Custom Items are not built yet, but the scan reads the field so that when
    // they are, they join the global uniqueness check with no change here.
    ...custom.items.map((i) => ({ id: i.id, name: i.name, type: 'Item' })),
  ];

  const byId = new Map<string, { id: string; name: string; type: string }[]>();
  for (const item of everyCustomItem) {
    const existing = byId.get(item.id);
    if (existing) existing.push(item);
    else byId.set(item.id, [item]);
  }

  const ambiguous = new Map<string, string>();
  for (const [id, items] of byId) {
    if (items.length < 2) continue;
    const description = items.map((i) => `the ${i.type} "${i.name}"`).join(' and ');
    ambiguous.set(
      id,
      `more than one piece of your content claims the ID "${id}": ${description}. ` +
        'Custom IDs must be unique across every kind of content',
    );
  }

  // Returns accepted and rejected separately. Working out rejections by
  // filtering on object identity afterwards would miss a list that contains the
  // SAME object twice, which is exactly the duplicate case being guarded.
  const accept = <T extends { id: string }>(
    printed: readonly T[],
    additions: readonly T[],
  ): { accepted: T[]; refused: Rejection<T>[] } => {
    const taken = new Set(printed.map((item) => item.id));
    const accepted: T[] = [];
    const refused: Rejection<T>[] = [];
    for (const item of additions) {
      const clash = ambiguous.get(item.id);
      if (clash !== undefined) {
        refused.push({ item, reason: clash });
        continue;
      }
      if (taken.has(item.id)) {
        refused.push({ item, reason: `its ID "${item.id}" is already in use` });
        continue;
      }
      taken.add(item.id);
      accepted.push(item);
    }
    return { accepted, refused };
  };

  const skillResult = accept(base.skills, custom.skills);
  const skills = skillResult.accepted;
  rejected.skills = skillResult.refused;

  // AttackProfiles are COMPANIONS to Skills, not independent global custom
  // identities. They intentionally share the Skill ID, so they must not join
  // `everyCustomItem` above. A profile enters the live catalogue only when its
  // matching custom Skill survived composition. If the Skill was refused for a
  // core/custom collision, its executable half disappears with it rather than
  // becoming a live orphan.
  const acceptedSkillIds = new Set(skills.map((s) => s.id));
  const seenAttackIds = new Set(base.attacks.attacks.map((a) => a.id));
  const customAttacks: AttackProfile[] = [];
  for (const attack of custom.attacks) {
    if (!acceptedSkillIds.has(attack.id)) continue;
    if (seenAttackIds.has(attack.id)) continue;
    seenAttackIds.add(attack.id);
    customAttacks.push(attack);
  }
  const composedAttacks: AttackContent = {
    ...base.attacks,
    attacks: [...base.attacks.attacks, ...customAttacks],
  };

  const itemResult = accept(base.items, custom.items);
  rejected.items = itemResult.refused;

  // Skills compose before Items because an Item may grant Ranks in a Skill.
  // Only definitions that survived composition are executable dependencies.
  const skillIds = new Set([...base.skills, ...skills].map((s) => s.id));

  // RULE DCC-INV-017 - a custom Item claiming to BE a weapon must name an
  // Attack that exists and is actually made with a held weapon, and must be a
  // held Hands Item itself. Checked HERE, at composition, because this is where
  // an Attack catalogue exists and where a hand-edited library file would
  // otherwise walk straight past a rule the editor enforces. Dropped from the
  // catalogue like any other unusable custom content; the stored file is
  // untouched, so fixing the reference restores it.
  const items: Item[] = [];
  const catalogue: InventoryCatalogue = {
    lookupItem: (id) => [...base.items, ...itemResult.accepted].find((i) => i.id === id),
    lookupAttack: (id) => composedAttacks.attacks.find((a) => a.id === id),
  };
  for (const item of itemResult.accepted) {
    const missingSkill = item.modifiers.find(
      (modifier) =>
        modifier.target.kind === 'skill-rank' && !skillIds.has(modifier.target.skillId),
    );
    if (missingSkill?.target.kind === 'skill-rank') {
      rejected.items.push({
        item,
        reason: `it grants Skill Ranks in missing Skill "${missingSkill.target.skillId}"`,
      });
      continue;
    }

    const problem = weaponLinkProblem(item, catalogue);
    if (problem === null) {
      items.push(item);
      continue;
    }
    rejected.items.push({ item, reason: `it ${problem}` });
  }

  const tableResult = accept(base.backgroundTables, custom.backgroundTables);
  const backgroundTables = tableResult.accepted;
  rejected.backgroundTables = tableResult.refused;

  // Skills are composed FIRST, so a Background is checked against the Skills
  // that will actually be in the catalogue - including the player's own.
  const tableIds = new Set([...base.backgroundTables, ...backgroundTables].map((t) => t.id));
  const itemIds = new Set([...base.items, ...items].map((i) => i.id));

  const usable: Background[] = [];
  for (const background of custom.backgrounds) {
    if (ambiguous.has(background.id)) {
      // Reported by `accept` below, alongside every other refusal.
      continue;
    }
    if (!tableIds.has(background.tableId)) {
      rejected.backgrounds.push({
        item: background,
        reason: `the table it belongs to ("${background.tableId}") does not exist`,
      });
      continue;
    }
    // RULE DCC-CUSTOM-009 extends to Items: a Background that hands over gear
    // nobody has is a choice the creator cannot complete, exactly as one
    // offering a missing Skill is. Same treatment, same reason.
    const missingItems = (background.startingEquipment ?? [])
      .map((g) => g.itemId)
      .filter((id) => !itemIds.has(id));
    if (missingItems.length > 0) {
      rejected.backgrounds.push({
        item: background,
        reason:
          `it grants ${missingItems.length === 1 ? 'an Item that does' : 'Items that do'} ` +
          `not exist: ${missingItems.join(', ')}`,
      });
      continue;
    }

    const missing = background.skillIds.filter((id) => !skillIds.has(id));
    if (missing.length > 0) {
      rejected.backgrounds.push({
        item: background,
        reason: `it offers ${missing.length === 1 ? 'a Skill that does' : 'Skills that do'} not exist: ${missing.join(', ')}`,
      });
      continue;
    }
    usable.push(background);
  }

  const backgroundResult = accept(base.backgrounds, [
    ...usable,
    // Ambiguous ones still go through `accept` so they are REPORTED rather
    // than dropped without explanation.
    ...custom.backgrounds.filter((b) => ambiguous.has(b.id)),
  ]);
  const backgrounds = backgroundResult.accepted;
  rejected.backgrounds.push(...backgroundResult.refused);

  return {
    pkg: {
      ...base,
      skills: [...base.skills, ...skills],
      backgroundTables: [...base.backgroundTables, ...backgroundTables],
      backgrounds: [...base.backgrounds, ...backgrounds],
      attacks: composedAttacks,
      items: [...base.items, ...items],
    },
    rejected,
  };
}

/**
 * Replaces some of the player's content and rebuilds the catalogue.
 *
 * Takes a PATCH. Fields left out keep their current value, which is what makes
 * "load Skills, then load Backgrounds" safe in either order.
 */
export function setCustomContent(patch: Partial<CustomContent>): RejectedContent {
  customContent = { ...customContent, ...patch };
  const { pkg, rejected } = composeContentPackage(dccCorePackage, withSharedItems(customContent));
  contentRegistry = new ContentRegistry(pkg);
  contentVersion += 1;
  return rejected;
}

/**
 * Items that belong to the TABLE, not to this player: loot, GM awards, things
 * another crawler put in a shared stash. The host supplies them.
 *
 * HELD SEPARATELY FROM `customContent`, and that is the point. Every service
 * that saves the player's own Items ends with `setCustomContent({ items })`
 * carrying the player's library. If shared Items lived in that same field, the
 * first custom Item a player created would silently drop every shared Item from
 * the live catalogue, and each crawler holding one would lose what it does.
 *
 * Never saved by anything in this package: the player's library stays exactly
 * what the player authored.
 */
let sharedItems: readonly Item[] = [];
/**
 * The Skill and Attack halves of GM-made weapons, which travel with their Item.
 * A weapon the GM invented has its own damage, so it needs its own Attack, and
 * an Attack is a Skill (their ids are equal). Whoever holds the Item needs both
 * definitions or the weapon is a lump of metal.
 */
let sharedSkills: readonly Skill[] = [];
let sharedAttacks: readonly AttackProfile[] = [];

/** The shared Items currently composed into the catalogue. */
export function currentSharedItems(): readonly Item[] {
  return sharedItems;
}

/** The shared weapon Skills currently composed into the catalogue. */
export function currentSharedSkills(): readonly Skill[] {
  return sharedSkills;
}

/**
 * The content to compose: shared content first, then the player's own.
 *
 * A player's own Item with the SAME ID as a shared one is left out of the
 * composition (never out of their file). That happens legitimately - a crawler
 * holding a shared Item is exported with its definition embedded, and importing
 * it writes that definition into the player's library. Without this the two
 * would collide under RULE DCC-CUSTOM-011 and BOTH be refused, taking the Item
 * away from the crawler that holds it. The table's definition is the live one.
 * Skills and Attacks follow the same rule for the same reason.
 */
function withSharedItems(custom: CustomContent): CustomContent {
  if (sharedItems.length === 0 && sharedSkills.length === 0) return custom;
  const sharedIds = new Set(sharedItems.map((item) => item.id));
  const sharedSkillIds = new Set(sharedSkills.map((skill) => skill.id));
  return {
    ...custom,
    skills: [...sharedSkills, ...custom.skills.filter((skill) => !sharedSkillIds.has(skill.id))],
    attacks: [...sharedAttacks, ...custom.attacks.filter((attack) => !sharedSkillIds.has(attack.id))],
    items: [...sharedItems, ...custom.items.filter((item) => !sharedIds.has(item.id))],
  };
}

function dedupe<T extends { id: string }>(entries: readonly T[]): T[] {
  const seen = new Set<string>();
  return entries.filter((entry) => (seen.has(entry.id) ? false : (seen.add(entry.id), true)));
}

/** What the table shares with this sheet. */
export interface SharedCatalogue {
  items: readonly Item[];
  skills?: readonly Skill[];
  attacks?: readonly AttackProfile[];
}

/** Replaces everything the table shares and rebuilds the catalogue. Returns the shared Items that were refused. */
export function setSharedContent(shared: SharedCatalogue): Rejection<Item>[] {
  sharedItems = dedupe(shared.items);
  sharedSkills = dedupe(shared.skills ?? []);
  const skillIds = new Set(sharedSkills.map((skill) => skill.id));
  // An Attack with no Skill is not a thing the engine can roll.
  sharedAttacks = dedupe(shared.attacks ?? []).filter((attack) => skillIds.has(attack.id));
  const { pkg, rejected } = composeContentPackage(dccCorePackage, withSharedItems(customContent));
  contentRegistry = new ContentRegistry(pkg);
  contentVersion += 1;
  const sharedIds = new Set(sharedItems.map((item) => item.id));
  return rejected.items.filter((rejection) => sharedIds.has(rejection.item.id));
}

/** Replaces the shared Items (and drops shared weapon Skills). Returns any that were refused. */
export function setSharedItems(items: readonly Item[]): Rejection<Item>[] {
  return setSharedContent({ items });
}

/**
 * Installs the complete custom Skill-library snapshot atomically in memory.
 *
 * Skills and their AttackProfiles live in one persisted record, and the live
 * catalogue must observe the same coherence: never "new Skill + old profile"
 * or the reverse between two setter calls.
 */
export function setCustomSkillContent(
  skills: readonly Skill[],
  attacks: readonly AttackProfile[],
): Rejection<Skill>[] {
  return setCustomContent({ skills, attacks }).skills;
}

/** Backwards-compatible convenience used by the legacy Utility/Passive screen. */
export function setCustomSkills(
  skills: readonly Skill[],
  attacks: readonly AttackProfile[] = [],
): Rejection<Skill>[] {
  return setCustomSkillContent(skills, attacks);
}

export function setCustomBackgroundContent(
  tables: readonly BackgroundTable[],
  backgrounds: readonly Background[],
): RejectedContent {
  return setCustomContent({ backgroundTables: tables, backgrounds });
}

/** Clears every piece of custom content. Used at startup and by tests. */
export function resetCustomContent(): void {
  customContent = EMPTY_CUSTOM_CONTENT;
  sharedItems = [];
  sharedSkills = [];
  sharedAttacks = [];
  const { pkg } = composeContentPackage(dccCorePackage, customContent);
  contentRegistry = new ContentRegistry(pkg);
  contentVersion += 1;
}

/** True when nothing was refused. */
export function acceptedEverything(rejected: RejectedContent): boolean {
  return isEmpty(rejected);
}

/**
 * Every content ID the catalogue currently knows, of every type.
 *
 * RULE DCC-CUSTOM-011 - used when generating a new custom ID, so a Skill cannot
 * be handed an ID a Background already uses. Add a content type here and every
 * generator starts avoiding it.
 */
export function allContentIds(): Set<string> {
  const ids = new Set<string>();
  for (const skill of contentRegistry.allSkills()) ids.add(skill.id);
  for (const item of contentRegistry.allItems()) ids.add(item.id);
  for (const crawlerType of ['human', 'animal'] as const) {
    for (const table of contentRegistry.tablesFor(crawlerType)) {
      ids.add(table.id);
      for (const background of contentRegistry.backgroundsForTable(table.id)) ids.add(background.id);
    }
  }
  return ids;
}

/** The custom Skills currently in the catalogue. */
export function customSkillsInCatalogue(): Skill[] {
  const printedIds = new Set(dccCorePackage.skills.map((s) => s.id));
  return contentRegistry.allSkills().filter((s) => !printedIds.has(s.id));
}

